import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Phone, PhoneOutgoing, PhoneIncoming, PhoneMissed, Clock, Sparkles, Loader2, Search,
  NotebookPen, PlayCircle, AudioLines, FileText, TrendingUp, RefreshCw, Info,
  CheckCircle2, X, Filter, Play, Pause, Trash2, Voicemail, ListChecks, Users, CalendarDays, Link2, Download,
  ChevronLeft, ChevronRight,
} from "lucide-react";
import api from "@/lib/api";
import { RotationMarker } from "@/components/RotationMarker";
import { useAuth } from "@/context/AuthContext";
import { useCall } from "@/context/CallContext";
import { PageHeader, PageBody } from "@/components/AppShell";
import { DialerButton } from "@/components/Dialer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { fr } from "date-fns/locale";
import { frenchDate } from "@/lib/shariti";
import { ContactLinkDialog } from "@/components/ContactLinkDialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

function fmtDur(s) {
  if (!s && s !== 0) return "—";
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}min ${String(sec).padStart(2, "0")}s`;
}
// Durée CUMULÉE (temps total) : affiche les heures dès que ≥ 1 h pour rester lisible.
function fmtDurLong(s) {
  if (!s && s !== 0) return "—";
  const total = Math.round(s);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}min`;
  return `${m}min ${String(sec).padStart(2, "0")}s`;
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

const STATUS_BADGE = {
  completed: { label: "Terminé", bg: "rgba(31,157,85,0.12)", fg: "#0F5A2F", icon: PhoneOutgoing },
  "in-progress": { label: "En cours", bg: "rgba(238,114,46,0.12)", fg: "#B4531B", icon: Phone },
  "no-answer": { label: "Sans réponse", bg: "var(--surface-2)", fg: "rgba(24,24,24,0.6)", icon: PhoneMissed },
  busy: { label: "Occupé", bg: "var(--surface-2)", fg: "rgba(24,24,24,0.6)", icon: PhoneMissed },
  failed: { label: "Échec", bg: "rgba(214,69,69,0.12)", fg: "#B23434", icon: PhoneMissed },
  canceled: { label: "Annulé", bg: "var(--surface-2)", fg: "rgba(24,24,24,0.6)", icon: PhoneMissed },
  initiated: { label: "Initié", bg: "var(--surface-2)", fg: "rgba(24,24,24,0.6)", icon: Phone },
};
const SENTIMENT = {
  positif: { label: "Positif", bg: "rgba(31,157,85,0.12)", fg: "#0F5A2F" },
  neutre: { label: "Neutre", bg: "var(--surface-2)", fg: "rgba(24,24,24,0.66)" },
  "négatif": { label: "Négatif", bg: "rgba(214,69,69,0.12)", fg: "#B23434" },
};

/** Classe un appel : outbound | inbound (entrant décroché) | missed | voicemail. */
function callKind(c) {
  if (c.is_voicemail) return "voicemail";
  if (c.direction === "inbound") return c.status === "completed" ? "inbound" : "missed";
  return "outbound";
}

const KIND_META = {
  outbound: { label: "Sortant", icon: PhoneOutgoing, bg: "rgba(238,114,46,0.12)", fg: "#B4531B" },
  inbound: { label: "Entrant", icon: PhoneIncoming, bg: "rgba(31,157,85,0.12)", fg: "#0F5A2F" },
  missed: { label: "Manqué", icon: PhoneMissed, bg: "rgba(214,69,69,0.12)", fg: "#B23434" },
  voicemail: { label: "Messagerie", icon: Voicemail, bg: "rgba(47,111,237,0.12)", fg: "#2F6FED" },
};

function StatusPill({ status }) {
  const m = STATUS_BADGE[status] || STATUS_BADGE.initiated;
  const Icon = m.icon;
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold"
      style={{ background: m.bg, color: m.fg }} data-testid="call-status-pill">
      <Icon className="h-3 w-3" />{m.label}
    </span>
  );
}

/** Bouton de suppression TOTALE d'un appel avec modale de confirmation. */
function DeleteCallButton({ callId, onDeleted, variant = "icon" }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const del = async () => {
    setBusy(true);
    try {
      await api.delete(`/calls/${callId}`);
      toast.success("Appel supprimé définitivement");
      setOpen(false);
      onDeleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible.");
    } finally { setBusy(false); }
  };
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        {variant === "icon" ? (
          <button
            type="button"
            onClick={(e) => e.stopPropagation()}
            aria-label="Supprimer l'appel"
            data-testid={`call-delete-${callId}`}
            className="grid h-8 w-8 place-items-center rounded-full text-muted-ink transition-colors hover:bg-[rgba(214,69,69,0.1)] hover:text-[#D64545] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D64545]"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        ) : (
          <Button type="button" variant="outline" onClick={(e) => e.stopPropagation()}
            data-testid={`call-delete-${callId}`}
            className="border-[rgba(214,69,69,0.4)] text-[#D64545] hover:bg-[rgba(214,69,69,0.08)] hover:text-[#D64545]">
            <Trash2 className="mr-1.5 h-4 w-4" />Supprimer l'appel
          </Button>
        )}
      </AlertDialogTrigger>
      <AlertDialogContent onClick={(e) => e.stopPropagation()} data-testid="call-delete-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle>Supprimer cet appel ?</AlertDialogTitle>
          <AlertDialogDescription>
            L'appel, son <strong>enregistrement audio</strong>, sa transcription et son analyse IA seront
            <strong> définitivement supprimés</strong> (y compris côté Twilio). Cette action est irréversible.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="call-delete-cancel">Annuler</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); del(); }}
            disabled={busy}
            className="bg-[#D64545] text-white hover:bg-[#b93a3a]"
            data-testid="call-delete-confirm-btn"
          >
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1.5 h-4 w-4" />}
            Supprimer définitivement
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// --- Filtre de période (page Appels) : présets + plage personnalisée ---
const toISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const mondayOf = (d) => {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const wd = (x.getDay() + 6) % 7; // 0 = lundi
  x.setDate(x.getDate() - wd);
  return x;
};
const PERIOD_PRESETS = [
  { key: "all", label: "Toutes les dates" },
  { key: "today", label: "Aujourd'hui" },
  { key: "this_week", label: "Cette semaine" },
  { key: "last_week", label: "Semaine dernière" },
  { key: "last7", label: "7 derniers jours" },
  { key: "this_month", label: "Ce mois-ci" },
  { key: "last30", label: "30 derniers jours" },
];
function computePeriod(key) {
  const now = new Date();
  const today = toISODate(now);
  if (key === "today") return { from: today, to: today };
  if (key === "this_week") {
    const mon = mondayOf(now); const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
    return { from: toISODate(mon), to: toISODate(sun) };
  }
  if (key === "last_week") {
    const mon = mondayOf(now); mon.setDate(mon.getDate() - 7);
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
    return { from: toISODate(mon), to: toISODate(sun) };
  }
  if (key === "last7") {
    const f = new Date(now); f.setDate(now.getDate() - 6);
    return { from: toISODate(f), to: today };
  }
  if (key === "this_month") {
    const f = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: toISODate(f), to: today };
  }
  if (key === "last30") {
    const f = new Date(now); f.setDate(now.getDate() - 29);
    return { from: toISODate(f), to: today };
  }
  return { from: null, to: null };
}


export default function Appels() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { config } = useCall();
  const [searchParams, setSearchParams] = useSearchParams();
  const [calls, setCalls] = useState(null);
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);
  const reqSeqRef = useRef(0);   // séquence des requêtes /calls (ignore les réponses périmées)
  const [totals, setTotals] = useState({
    counts: { all: 0, outbound: 0, inbound: 0, voicemail: 0 },
    analyzed: 0, unread: 0, total: 0, pages: 1, page: 1, summary: null,
  });
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");   // recherche « débouncée » envoyée au serveur
  const [tab, setTab] = useState("all");
  const [selected, setSelected] = useState(null);
  const [linkCall, setLinkCall] = useState(null); // appel orphelin à rattacher
  // Sélection multiple + suppression en masse (admin)
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const FILTER_KEYS = ["cdp_id", "day", "weekday", "hour", "outcome", "cause", "decideur", "flag", "data", "status", "date_from", "date_to"];
  const filters = useMemo(() => {
    const f = {};
    for (const k of FILTER_KEYS) {
      const v = searchParams.get(k);
      if (v !== null && v !== "" && v !== "x") f[k] = v;
    }
    return f;
    // eslint-disable-next-line
  }, [searchParams]);

  // Ouverture directe d'un appel via ?call={id} (depuis le Diagnostic IA « Écoutes clés »).
  useEffect(() => {
    const cid = searchParams.get("call");
    if (!cid) return;
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/calls/${cid}`);
        if (!cancel && data) setSelected(data);
      } catch { /* appel introuvable : ignore */ }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line
  }, [searchParams]);

  const load = useCallback(async () => {
    // Garde-fou anti réponses PÉRIMÉES : lors d'un changement de filtre alors qu'on
    // est sur une page > 1, deux requêtes partent (reset page + nouveau filtre). Sans
    // garde, une réponse périmée (ex. « tous CDP » = 92) pouvait écraser la bonne
    // (ex. Hillary = 82) → compteurs, taux et durée moyenne incohérents avec le filtre
    // réel, et figés d'une page à l'autre. On n'applique QUE la réponse de la DERNIÈRE
    // requête émise (séquence croissante).
    const myReq = ++reqSeqRef.current;
    setCalls(null);
    try {
      const { data } = await api.get("/calls", {
        params: { page, page_size: PAGE_SIZE, kind: tab, ...filters, q: dq || undefined },
      });
      if (myReq !== reqSeqRef.current) return;   // réponse périmée → ignorée
      setCalls(data.calls || []);
      setTotals({
        counts: data.counts || { all: 0, outbound: 0, inbound: 0, voicemail: 0 },
        analyzed: data.analyzed ?? 0,
        unread: data.unread_voicemail ?? 0,
        total: data.total ?? (data.calls || []).length,
        pages: data.pages ?? 1,
        page: data.page ?? page,
        summary: data.summary || null,
      });
    } catch {
      if (myReq !== reqSeqRef.current) return;   // réponse périmée → ignorée
      setCalls([]);
      setTotals({ counts: { all: 0, outbound: 0, inbound: 0, voicemail: 0 }, analyzed: 0, unread: 0, total: 0, pages: 1, page: 1, summary: null });
    }
  }, [filters, dq, tab, page]);
  useEffect(() => { load(); }, [load]);

  // Retour à la page 1 quand la recherche, l'onglet ou les filtres changent.
  useEffect(() => { setPage(1); }, [dq, tab, searchParams]);
  // Garde-fou : ne jamais rester au-delà de la dernière page disponible.
  useEffect(() => {
    if (totals.pages && page > totals.pages) setPage(totals.pages);
  }, [totals.pages, page]);

  // Debounce de la recherche : envoyée au SERVEUR (recherche sur TOUT l'historique).
  useEffect(() => {
    const t = setTimeout(() => setDq(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  // Marque un message vocal / appel manqué comme écouté (retire le badge « nouveau »).
  const markHeard = useCallback(async (id) => {
    setCalls((prev) => (prev || []).map((c) => (c.id === id ? { ...c, heard: true } : c)));
    try { await api.put(`/calls/${id}/heard`); } catch { /* noop */ }
  }, []);

  const clearFilters = () => setSearchParams({});
  const removeFilter = (key) => {
    const next = new URLSearchParams(searchParams);
    if (key === "period") { next.delete("date_from"); next.delete("date_to"); }
    else next.delete(key);
    setSearchParams(next);
  };

  // --- Filtre par CDP (admin) : liste des CDP ayant passé des appels ---
  const [cdps, setCdps] = useState([]);
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const { data } = await api.get("/calls/cdps");
        setCdps(data.cdps || []);
      } catch (_) { setCdps([]); }
    })();
  }, [isAdmin]);
  const selectedCdp = filters.cdp_id || "all";
  const onCdpChange = (val) => {
    const next = new URLSearchParams(searchParams);
    if (!val || val === "all") next.delete("cdp_id");
    else next.set("cdp_id", val);
    setSearchParams(next);
  };

  // --- Filtre par période (présets + plage personnalisée) ---
  const [periodOpen, setPeriodOpen] = useState(false);
  const periodFrom = filters.date_from || null;
  const periodTo = filters.date_to || null;
  const selectedPeriodKey = useMemo(() => {
    if (!periodFrom && !periodTo) return "all";
    for (const p of PERIOD_PRESETS) {
      if (p.key === "all") continue;
      const r = computePeriod(p.key);
      if (r.from === periodFrom && r.to === periodTo) return p.key;
    }
    return "custom";
  }, [periodFrom, periodTo]);
  const periodLabel = useMemo(() => {
    if (selectedPeriodKey === "all") return "Toutes les dates";
    if (selectedPeriodKey === "custom") return `${periodFrom ? frenchDate(periodFrom) : "…"} → ${periodTo ? frenchDate(periodTo) : "…"}`;
    return PERIOD_PRESETS.find((p) => p.key === selectedPeriodKey)?.label || "Période";
  }, [selectedPeriodKey, periodFrom, periodTo]);
  const parseISO = (s) => { if (!s) return undefined; const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
  const calRange = useMemo(() => {
    const from = parseISO(periodFrom); const to = parseISO(periodTo);
    return from ? { from, to: to || from } : undefined;
    // eslint-disable-next-line
  }, [periodFrom, periodTo]);
  const setPeriodParams = (from, to) => {
    const next = new URLSearchParams(searchParams);
    if (from) next.set("date_from", from); else next.delete("date_from");
    if (to) next.set("date_to", to); else next.delete("date_to");
    setSearchParams(next);
  };
  const applyPreset = (key) => {
    if (key === "all") { setPeriodParams(null, null); setPeriodOpen(false); return; }
    const r = computePeriod(key);
    setPeriodParams(r.from, r.to);
    setPeriodOpen(false);
  };
  const onRangeSelect = (r) => {
    if (!r) { setPeriodParams(null, null); return; }
    const from = r.from ? toISODate(r.from) : null;
    const to = r.to ? toISODate(r.to) : from;
    setPeriodParams(from, to);
  };

  // --- Mini-résumé quand un CDP est sélectionné (totaux réels backend, tout l'historique) ---
  const cdpStats = useMemo(() => {
    if (!filters.cdp_id) return null;
    const s = totals.summary;
    if (!s) return null;
    const name = (calls || []).find((c) => c.cdp_id === filters.cdp_id)?.cdp_name
      || cdps.find((c) => c.cdp_id === filters.cdp_id)?.cdp_name || "CDP";
    return { total: s.total, answered: s.answered, noAnswer: s.no_answer,
             avg: s.avg_duration, totalDur: s.total_duration ?? 0, rate: s.rate, name };
  }, [totals.summary, filters.cdp_id, cdps, calls]);

  const activeChips = useMemo(() => buildChips(filters, calls), [filters, calls]);
  const hasFilters = activeChips.length > 0;

  // Comptages RÉELS venant du backend (tout l'historique filtré), pas la page.
  const counts = totals.counts || { all: 0, outbound: 0, inbound: 0, voicemail: 0 };
  const unreadVoicemail = totals.unread || 0;

  // La page renvoyée par le serveur est DÉJÀ filtrée par onglet (kind) et recherche (q).
  const filtered = calls || [];

  const analyzedCount = totals.analyzed || 0;

  // --- Sélection multiple / suppression en masse ---
  const filteredIds = filtered.map((c) => c.id);
  const selectedCount = selectedIds.size;
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selectedIds.has(id));
  const someFilteredSelected = filteredIds.some((id) => selectedIds.has(id));
  const toggleSelect = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleSelectAll = () => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (allFilteredSelected) filteredIds.forEach((id) => next.delete(id));
    else filteredIds.forEach((id) => next.add(id));
    return next;
  });
  const exitSelectMode = () => { setSelectMode(false); setSelectedIds(new Set()); };
  const bulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const { data } = await api.post("/calls/bulk-delete", { ids: [...selectedIds] });
      toast.success(`${data.deleted} appel(s) supprimé(s)`);
      setBulkConfirmOpen(false);
      exitSelectMode();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible.");
    } finally { setBulkDeleting(false); }
  };

  return (
    <>
      <PageHeader title="Appels" subtitle="Historique, enregistrements et analyses IA de vos appels de prospection.">
        <div className="flex flex-wrap gap-3">
          <StatTile icon={Phone} label="Appels" value={calls ? counts.all : "—"} />
          <StatTile icon={Sparkles} label="Analysés par l'IA" value={calls ? analyzedCount : "—"} />
          <StatTile icon={Voicemail} label="Messagerie" value={calls ? counts.voicemail : "—"} badge={unreadVoicemail} />
        </div>
      </PageHeader>
      <PageBody>
        {!config.ready && (
          <div className="mb-5 flex items-start gap-2 rounded-2xl border border-[rgba(240,190,20,0.6)] bg-[rgba(252,219,83,0.25)] px-4 py-3 text-sm text-[#5A4200]" data-testid="calls-pending-banner">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <span className="font-bold">Téléphonie en attente d'activation.</span> Le numéro Twilio est en cours de validation réglementaire.
              Dès qu'il sera approuvé, le bouton « Appeler » de la prospection permettra de lancer des appels depuis le navigateur. L'historique et l'analyse IA sont déjà opérationnels.
            </div>
          </div>
        )}

        {/* Chips de filtres actifs (drill-down depuis le cockpit) */}
        {hasFilters && (
          <div className="mb-4 flex flex-wrap items-center gap-2 rounded-2xl border border-[var(--border-soft)] bg-[rgba(238,114,46,0.04)] px-4 py-3" data-testid="calls-filter-bar">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-[var(--shariti-orange)]">
              <Filter className="h-3.5 w-3.5" />Filtres actifs
            </span>
            {activeChips.map((chip) => (
              <span key={chip.key} className="inline-flex items-center gap-1 rounded-full border border-[var(--border-soft)] bg-white px-2.5 py-1 text-xs font-semibold" data-testid={`calls-chip-${chip.key}`}>
                {chip.label}
                <button onClick={() => removeFilter(chip.key)} className="ml-0.5 rounded-full p-0.5 text-muted-ink hover:bg-[var(--surface-2)] hover:text-[var(--shariti-ink)]" data-testid={`calls-chip-remove-${chip.key}`}>
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            <Button variant="ghost" size="sm" onClick={clearFilters} className="h-7 px-2 text-xs" data-testid="calls-clear-filters">Réinitialiser</Button>
          </div>
        )}

        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="relative min-w-[220px] max-w-sm flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-ink" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un contact, une enseigne, un numéro…"
              className="h-10 pl-9" data-testid="calls-search" />
          </div>
          <div className="flex items-center gap-3">
            {isAdmin && (
              <Select value={selectedCdp} onValueChange={onCdpChange}>
                <SelectTrigger className="h-10 w-[190px]" data-testid="calls-cdp-filter" aria-label="Filtrer par CDP">
                  <Users className="mr-1.5 h-4 w-4 shrink-0 text-muted-ink" />
                  <SelectValue placeholder="Tous les CDP" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="calls-cdp-option-all">Tous les CDP</SelectItem>
                  {cdps.map((c) => (
                    <SelectItem key={c.cdp_id} value={c.cdp_id} data-testid={`calls-cdp-option-${c.cdp_id}`}>
                      {c.cdp_name}{c.count ? ` (${c.count})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Popover open={periodOpen} onOpenChange={setPeriodOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className={`h-10 gap-1.5 ${selectedPeriodKey !== "all" ? "border-[var(--shariti-orange)] text-[var(--shariti-orange)]" : ""}`} data-testid="calls-period-filter">
                  <CalendarDays className="h-4 w-4 shrink-0" />
                  <span className="max-w-[150px] truncate">{periodLabel}</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-auto p-3" data-testid="calls-period-popover">
                <div className="mb-3 grid grid-cols-2 gap-1.5">
                  {PERIOD_PRESETS.map((p) => (
                    <button key={p.key} type="button" onClick={() => applyPreset(p.key)}
                      data-testid={`calls-period-${p.key}`}
                      className={`rounded-md border px-2.5 py-1.5 text-xs font-semibold transition-colors ${
                        selectedPeriodKey === p.key
                          ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.1)] text-[var(--shariti-orange)]"
                          : "border-[var(--border-soft)] bg-white text-ink hover:bg-[var(--surface-2)]"}`}>
                      {p.label}
                    </button>
                  ))}
                </div>
                <div className="border-t border-[var(--border-soft)] pt-2">
                  <div className="mb-1 text-[11px] font-semibold text-muted-ink">Plage personnalisée</div>
                  <Calendar mode="range" locale={fr} numberOfMonths={1} selected={calRange} onSelect={onRangeSelect} data-testid="calls-period-calendar" />
                </div>
              </PopoverContent>
            </Popover>
            <Button variant="outline" onClick={load} data-testid="calls-refresh"><RefreshCw className="mr-1.5 h-4 w-4" />Actualiser</Button>
            {isAdmin && (calls?.length > 0) && (
              <Button variant="outline" onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
                data-testid="calls-select-toggle"
                className={selectMode ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.08)] text-[var(--shariti-orange)] hover:bg-[rgba(238,114,46,0.12)] hover:text-[var(--shariti-orange)]" : ""}>
                <ListChecks className="mr-1.5 h-4 w-4" />{selectMode ? "Terminer" : "Sélectionner"}
              </Button>
            )}
            <DialerButton onSpotCreated={load} data-testid="calls-dialer-btn" />
          </div>
        </div>

        {/* Onglets : filtre par type d'appel (sortant / entrant / messagerie) */}
        <div className="mb-4">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList data-testid="calls-tabs">
              <TabsTrigger value="all" data-testid="calls-tab-all">Tous{counts.all ? ` (${counts.all})` : ""}</TabsTrigger>
              <TabsTrigger value="outbound" data-testid="calls-tab-outbound">Sortants{counts.outbound ? ` (${counts.outbound})` : ""}</TabsTrigger>
              <TabsTrigger value="inbound" data-testid="calls-tab-inbound">Entrants{counts.inbound ? ` (${counts.inbound})` : ""}</TabsTrigger>
              <TabsTrigger value="voicemail" data-testid="calls-tab-voicemail" className="gap-1.5">
                Messagerie{counts.voicemail ? ` (${counts.voicemail})` : ""}
                {unreadVoicemail > 0 && (
                  <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[#D64545] px-1 text-[10px] font-bold text-white" data-testid="calls-tab-voicemail-badge">{unreadVoicemail}</span>
                )}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {cdpStats && (
          <div className="mb-4 rounded-2xl border border-[var(--border-soft)] bg-white p-3 shadow-[var(--shadow-sm)]" data-testid="cdp-stats-summary">
            <div className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-[var(--shariti-orange)]">
              <Users className="h-3.5 w-3.5" />Résumé — {cdpStats.name}
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
              <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2" data-testid="cdp-stat-total">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold text-muted-ink"><Phone className="h-3.5 w-3.5" />Appels</div>
                <div className="mt-0.5 text-xl font-extrabold text-ink">{cdpStats.total}</div>
              </div>
              <div className="rounded-xl border border-[rgba(31,157,85,0.3)] bg-[rgba(31,157,85,0.06)] px-3 py-2" data-testid="cdp-stat-rate">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold text-[#1F7A44]"><CheckCircle2 className="h-3.5 w-3.5" />Taux de réponse</div>
                <div className="mt-0.5 text-xl font-extrabold text-[#0F5A2F]">{cdpStats.rate}%</div>
                <div className="text-[10px] text-muted-ink">{cdpStats.answered} répondu{cdpStats.answered > 1 ? "s" : ""}</div>
              </div>
              <div className="rounded-xl border border-[rgba(214,69,69,0.3)] bg-[rgba(214,69,69,0.05)] px-3 py-2" data-testid="cdp-stat-noanswer">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold text-[#9B2226]"><PhoneMissed className="h-3.5 w-3.5" />Sans réponse</div>
                <div className="mt-0.5 text-xl font-extrabold text-[#9B2226]">{cdpStats.noAnswer}</div>
              </div>
              <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2" data-testid="cdp-stat-avgdur">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold text-muted-ink"><Clock className="h-3.5 w-3.5" />Durée moyenne</div>
                <div className="mt-0.5 text-xl font-extrabold text-ink">{fmtDur(cdpStats.avg)}</div>
                <div className="text-[10px] text-muted-ink">sur appels aboutis</div>
              </div>
              <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2" data-testid="cdp-stat-totaldur">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold text-muted-ink"><Clock className="h-3.5 w-3.5" />Temps total</div>
                <div className="mt-0.5 text-xl font-extrabold text-ink">{fmtDurLong(cdpStats.totalDur)}</div>
                <div className="text-[10px] text-muted-ink">sur appels aboutis</div>
              </div>
            </div>
          </div>
        )}


        {/* Barre d'action de masse (mode sélection, admin) */}
        {isAdmin && selectMode && (
          <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl border border-[var(--border-soft)] bg-[var(--surface-2)] px-4 py-2.5" data-testid="calls-bulk-bar">
            <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold">
              <Checkbox
                checked={allFilteredSelected ? true : (someFilteredSelected ? "indeterminate" : false)}
                onCheckedChange={toggleSelectAll}
                data-testid="calls-select-all"
                aria-label="Tout sélectionner"
              />
              Tout sélectionner ({filteredIds.length})
            </label>
            <span className="text-sm text-muted-ink" data-testid="calls-selected-count">
              {selectedCount} sélectionné{selectedCount > 1 ? "s" : ""}
            </span>
            <div className="ml-auto flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={exitSelectMode} data-testid="calls-select-cancel">Annuler</Button>
              <Button size="sm" disabled={selectedCount === 0} onClick={() => setBulkConfirmOpen(true)}
                className="bg-[#D64545] text-white hover:bg-[#b93a3a] disabled:opacity-40"
                data-testid="calls-bulk-delete-btn">
                <Trash2 className="mr-1.5 h-4 w-4" />Supprimer la sélection{selectedCount ? ` (${selectedCount})` : ""}
              </Button>
            </div>
          </div>
        )}

        {calls === null ? (
          <div className="space-y-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20 w-full rounded-2xl" />)}</div>
        ) : filtered.length === 0 ? (
          <EmptyCalls hasFilters={hasFilters} onClear={clearFilters} />
        ) : (
          <div className="space-y-3" data-testid="calls-list">
            {filtered.map((c) => (
              <CallRow key={c.id} call={c} isAdmin={isAdmin} onOpen={() => setSelected(c)} onDeleted={load} onHeard={markHeard}
                selectMode={selectMode} selected={selectedIds.has(c.id)} onToggleSelect={toggleSelect} onLink={setLinkCall} />
            ))}
          </div>
        )}

        {/* Pagination serveur (50 appels par page) */}
        {calls !== null && totals.pages > 1 && (
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3" data-testid="calls-pagination">
            <span className="text-xs text-muted-ink" data-testid="calls-pagination-info">
              Page {Math.min(page, totals.pages)} sur {totals.pages} · {totals.total} appel{totals.total > 1 ? "s" : ""}
            </span>
            <div className="flex items-center gap-1.5">
              <Button variant="outline" size="sm" disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="calls-page-prev">
                <ChevronLeft className="mr-1 h-4 w-4" />Précédent
              </Button>
              <span className="px-2 text-sm font-semibold tabular-nums" data-testid="calls-page-current">
                {Math.min(page, totals.pages)} / {totals.pages}
              </span>
              <Button variant="outline" size="sm" disabled={page >= totals.pages}
                onClick={() => setPage((p) => Math.min(totals.pages, p + 1))} data-testid="calls-page-next">
                Suivant<ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </PageBody>

      <AlertDialog open={bulkConfirmOpen} onOpenChange={setBulkConfirmOpen}>
        <AlertDialogContent data-testid="calls-bulk-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer {selectedCount} appel{selectedCount > 1 ? "s" : ""} ?</AlertDialogTitle>
            <AlertDialogDescription>
              Les appels sélectionnés, leurs <strong>enregistrements audio</strong>, transcriptions et analyses IA
              seront <strong>définitivement supprimés</strong> (y compris côté Twilio). Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="calls-bulk-delete-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); bulkDelete(); }} disabled={bulkDeleting}
              className="bg-[#D64545] text-white hover:bg-[#b93a3a]" data-testid="calls-bulk-delete-confirm-btn">
              {bulkDeleting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1.5 h-4 w-4" />}
              Supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CallDetailDialog call={selected} open={!!selected} onClose={() => setSelected(null)} onChanged={load} isAdmin={isAdmin} onHeard={markHeard} onLink={setLinkCall} />
      <ContactLinkDialog call={linkCall} open={!!linkCall} onClose={() => setLinkCall(null)}
        onLinked={() => { setLinkCall(null); load(); }} />
    </>
  );
}

const WEEKDAY_LABELS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const OUTCOME_LABELS = {
  accord: "Accord", rendez_vous: "Rendez-vous", a_relancer: "À relancer",
  refus: "Refus", injoignable: "Injoignable", information: "Information",
};

// Valeurs « vides » renvoyées par l'IA qu'on n'affiche pas comme raison.
const EMPTY_AI = new Set(["", "non détecté", "non detecte", "n/a", "na", "aucune", "aucun", "-", "inconnu"]);
// Raison courte associée à un appel (pourquoi c'est positif/négatif), d'un coup d'œil.
function callReason(call) {
  const a = call.ai_analysis || {};
  const cause = (a.cause_refus || "").trim();
  if (cause && !EMPTY_AI.has(cause.toLowerCase())) return cause;
  const oc = (a.outcome || "").trim().toLowerCase();
  if (oc && !EMPTY_AI.has(oc)) return OUTCOME_LABELS[oc] || oc.replaceAll("_", " ");
  const na = (a.next_action || "").trim();
  if (na && !EMPTY_AI.has(na.toLowerCase())) return na;
  return null;
}

function buildChips(filters, calls) {
  const chips = [];
  if (filters.cdp_id) {
    const name = (calls || []).find((c) => c.cdp_id === filters.cdp_id)?.cdp_name;
    chips.push({ key: "cdp_id", label: `CDP : ${name || "sélectionné"}` });
  }
  if (filters.day) chips.push({ key: "day", label: `Jour : ${filters.day}` });
  if (filters.weekday !== undefined && filters.weekday !== null) {
    chips.push({ key: "weekday", label: WEEKDAY_LABELS[parseInt(filters.weekday)] || `Jour ${filters.weekday}` });
  }
  if (filters.hour) chips.push({ key: "hour", label: `Créneau : ${filters.hour}h` });
  if (filters.outcome) chips.push({ key: "outcome", label: `Issue : ${OUTCOME_LABELS[filters.outcome] || filters.outcome}` });
  if (filters.cause) chips.push({ key: "cause", label: `Refus : ${filters.cause}` });
  if (filters.decideur) chips.push({ key: "decideur", label: filters.decideur === "oui" ? "Décideur joint" : "Décideur non joint" });
  if (filters.status === "completed") chips.push({ key: "status", label: "Appels aboutis" });
  if (filters.flag === "complaint") chips.push({ key: "flag", label: "Plainte / signalement" });
  if (filters.data === "demo") chips.push({ key: "data", label: "Données démo" });
  if (filters.date_from || filters.date_to) {
    let plabel = null;
    for (const p of PERIOD_PRESETS) {
      if (p.key === "all") continue;
      const r = computePeriod(p.key);
      if (r.from === filters.date_from && r.to === filters.date_to) { plabel = p.label; break; }
    }
    if (!plabel) {
      const f = filters.date_from ? frenchDate(filters.date_from) : "…";
      const t = filters.date_to ? frenchDate(filters.date_to) : "…";
      plabel = `${f} → ${t}`;
    }
    chips.push({ key: "period", label: `Période : ${plabel}` });
  }
  return chips;
}

function StatTile({ icon: Icon, label, value, badge }) {
  return (
    <div className="relative flex items-center gap-3 rounded-2xl border border-[var(--border-soft)] bg-white/70 px-4 py-2.5 backdrop-blur">
      <div className="grid h-9 w-9 place-items-center rounded-xl bg-[rgba(238,114,46,0.12)] text-[var(--shariti-orange)]"><Icon className="h-4 w-4" /></div>
      <div>
        <div className="text-lg font-extrabold leading-none">{value}</div>
        <div className="text-[11px] text-muted-ink">{label}</div>
      </div>
      {badge > 0 && (
        <span className="absolute -right-1.5 -top-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#D64545] px-1 text-[10px] font-bold text-white shadow-[var(--shadow-sm)]" data-testid="voicemail-unread-badge">
          {badge}
        </span>
      )}
    </div>
  );
}

function CallRow({ call, isAdmin, onOpen, onDeleted, onHeard, selectMode, selected, onToggleSelect, onLink }) {
  const kind = callKind(call);
  const km = KIND_META[kind];
  const KIcon = km.icon;
  const isUnheard = (kind === "voicemail" || kind === "missed") && !call.heard;
  // Appel « orphelin » : numéro SORTANT composé sans magasin associé (le n° à
  // rattacher est le to_number). On exclut les entrants/messagerie (n° pertinent =
  // from_number, non géré par /link) et les appels de démonstration.
  const isOrphan = kind === "outbound" && !call.spot_id && call.contact_status !== "invalid" && !call.is_demo;
  const isInvalidNum = call.contact_status === "invalid" && !call.spot_id;
  const title = call.contact_name || call.spot_name || (call.direction === "inbound" ? (call.from_number || "Appel entrant") : "Contact");
  const initials = kind === "outbound"
    ? (call.contact_name || call.spot_name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()
    : null;
  const sent = call.ai_analysis?.sentiment ? (SENTIMENT[call.ai_analysis.sentiment] || SENTIMENT.neutre) : null;
  const reason = callReason(call);
  const number = call.direction === "inbound" ? (call.from_number || call.to_number) : call.to_number;
  const openIfNotInteractive = (e) => {
    // N'ouvre le détail que si le clic ne vient pas d'une zone interactive (lecteur / suppression).
    if (e.target.closest("[data-inline-player]") || e.target.closest("[data-row-action]")) return;
    // En mode sélection, un clic sur la ligne (co)che l'appel au lieu d'ouvrir le détail.
    if (selectMode && isAdmin) { onToggleSelect?.(call.id); return; }
    if (isUnheard) onHeard?.(call.id);
    onOpen();
  };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={openIfNotInteractive}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); if (isUnheard) onHeard?.(call.id); onOpen(); } }}
      data-testid={`call-row-${call.id}`}
      className={`card-hover flex w-full cursor-pointer items-center gap-4 rounded-2xl border bg-white px-4 py-3.5 text-left shadow-[var(--shadow-sm)] transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--shariti-orange)] ${selected ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.06)]" : isUnheard ? "border-[rgba(47,111,237,0.45)] bg-[rgba(47,111,237,0.03)]" : "border-[var(--border-soft)]"}`}
    >
      {isAdmin && selectMode && (
        <div data-row-action className="shrink-0" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={!!selected}
            onCheckedChange={() => onToggleSelect?.(call.id)}
            data-testid={`call-select-${call.id}`}
            aria-label="Sélectionner l'appel"
          />
        </div>
      )}
      <div className="relative grid h-11 w-11 shrink-0 place-items-center rounded-full text-sm font-bold" style={{ background: km.bg, color: km.fg }}>
        {initials || <KIcon className="h-5 w-5" />}
        {isUnheard && <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-white bg-[#D64545]" data-testid={`call-unheard-${call.id}`} />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="break-words text-sm font-extrabold">{title}</span>
          {call.spot_name && call.contact_name && <span className="break-words text-xs text-muted-ink">· {call.spot_name}</span>}
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wide" style={{ background: km.bg, color: km.fg }} data-testid={`call-kind-${call.id}`}>
            <KIcon className="h-2.5 w-2.5" />{km.label}
          </span>
          {isUnheard && <span className="shrink-0 rounded-full bg-[#D64545] px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-white">Nouveau</span>}
          {isOrphan && <span className="shrink-0 rounded-full bg-[rgba(238,114,46,0.15)] px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-[var(--shariti-orange)]" data-testid={`call-orphan-${call.id}`}>Sans magasin</span>}
          {isInvalidNum && <span className="shrink-0 rounded-full bg-[rgba(214,69,69,0.12)] px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-[#9B2226]">Numéro erroné</span>}
          {call.is_demo && <span className="shrink-0 rounded-full bg-[rgba(252,219,83,0.5)] px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-[#5A4200]" data-testid="call-demo-tag">Démo</span>}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-ink">
          <span className="inline-flex items-center gap-1"><KIcon className="h-3 w-3" />{number}</span>
          <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{fmtDateTime(call.started_at)}</span>
          {isAdmin && call.cdp_name && <span className="font-semibold">{call.cdp_name}</span>}
        </div>
        {/* Lecteur audio en ligne (aperçu direct, sans ouvrir le détail) */}
        {call.recording_sid && (
          <InlineAudioPlayer
            callId={call.id}
            totalSec={call.duration_sec}
            fileLabel={`appel_${call.spot_name || call.contact_name || number || "inconnu"}_${String(call.started_at || "").slice(0, 10)}`}
            onPlay={() => { if (isUnheard) onHeard?.(call.id); }}
          />
        )}
      </div>
      <div className="hidden shrink-0 flex-col items-end gap-1.5 sm:flex">
        <StatusPill status={call.status} />
        <div className="flex items-center gap-1.5 text-[11px] text-muted-ink">
          {call.duration_sec != null && <span>{fmtDur(call.duration_sec)}</span>}
          {call.recording_sid && <span className="inline-flex items-center gap-0.5 text-[var(--shariti-orange)]"><AudioLines className="h-3 w-3" />{kind === "voicemail" ? "Message" : "Enreg."}</span>}
        </div>
      </div>
      {sent && (
        <div className="hidden shrink-0 flex-col items-end gap-0.5 md:flex" data-testid={`call-sentiment-${call.id}`}>
          <span className="rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ background: sent.bg, color: sent.fg }}>{sent.label}</span>
          {reason && <span className="max-w-[240px] text-right text-[9px] font-semibold leading-snug text-muted-ink first-letter:uppercase [overflow-wrap:anywhere]" title={reason} data-testid={`call-reason-${call.id}`}>{reason}</span>}
        </div>
      )}
      {call.ai_summary && <Sparkles className="h-4 w-4 shrink-0 text-[var(--shariti-orange)]" />}
      {isOrphan && (
        <div data-row-action className="shrink-0">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={(e) => { e.stopPropagation(); onLink?.(call); }}
            data-testid={`call-link-${call.id}`}
            className="h-8 border-[rgba(238,114,46,0.5)] px-2.5 text-xs font-semibold text-[var(--shariti-orange)] hover:bg-[rgba(238,114,46,0.08)] hover:text-[var(--shariti-orange)]"
          >
            <Link2 className="mr-1 h-3.5 w-3.5" />Rattacher
          </Button>
        </div>
      )}
      {isAdmin && (
        <div data-row-action className="shrink-0">
          <DeleteCallButton callId={call.id} onDeleted={onDeleted} variant="icon" />
        </div>
      )}
    </div>
  );
}

/** Fmt court mm:ss pour le lecteur inline. */
function fmtClock(s) {
  if (!s && s !== 0) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
}

// Une seule lecture inline active à la fois (évite la cacophonie sur la liste).
let CURRENT_INLINE_AUDIO = null;

/**
 * Télécharge l'enregistrement audio d'un appel (blob authentifié via l'API).
 * Nom de fichier lisible (enseigne + date). Le backend renvoie un MP3 mono léger
 * (`dl=mp3`), facilement partageable sur WhatsApp. Utilisé par le petit bouton
 * discret de la liste et par le détail d'un appel — pour faire écouter un appel.
 */
async function downloadCallRecording(callId, fileLabel) {
  // `dl=true` : le backend renvoie un MP3 mono léger, facile à partager sur WhatsApp.
  const res = await api.get(`/calls/${callId}/recording?dl=true`, { responseType: "blob" });
  const blob = res.data;
  const ct = blob.type || res.headers?.["content-type"] || "audio/mpeg";
  const ext = /wav/i.test(ct) ? "wav" : "mp3";
  const safe = String(fileLabel || "appel").replace(/[^\w\-]+/g, "_").replace(/_+/g, "_").slice(0, 70) || "appel";
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${safe}.${ext}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/**
 * Lecteur audio compact en ligne : play/pause + barre de progression cliquable
 * + minuteur. L'audio (blob authentifié) n'est chargé qu'au 1er clic sur play.
 */
function InlineAudioPlayer({ callId, totalSec, onPlay, fileLabel }) {
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(totalSec || 0);
  const [error, setError] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const audioRef = useState(() => (typeof Audio !== "undefined" ? new Audio() : null))[0];

  useEffect(() => {
    const a = audioRef;
    if (!a) return;
    const onTime = () => setCur(a.currentTime || 0);
    const onMeta = () => { if (isFinite(a.duration)) setDur(a.duration); };
    const onEnd = () => { setPlaying(false); setCur(0); };
    const onPause = () => setPlaying(false);
    const onPlay = () => setPlaying(true);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    a.addEventListener("pause", onPause);
    a.addEventListener("play", onPlay);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("play", onPlay);
      try { a.pause(); } catch { /* noop */ }
      if (a.src) { URL.revokeObjectURL(a.src); a.removeAttribute("src"); }
      if (CURRENT_INLINE_AUDIO === a) CURRENT_INLINE_AUDIO = null;
    };
    // eslint-disable-next-line
  }, []);

  const toggle = async () => {
    const a = audioRef;
    if (!a) return;
    if (playing) { a.pause(); return; }
    onPlay?.();
    // Coupe toute autre lecture inline en cours.
    if (CURRENT_INLINE_AUDIO && CURRENT_INLINE_AUDIO !== a) { try { CURRENT_INLINE_AUDIO.pause(); } catch { /* noop */ } }
    CURRENT_INLINE_AUDIO = a;
    if (!a.src) {
      setLoading(true); setError(false);
      try {
        const res = await api.get(`/calls/${callId}/recording`, { responseType: "blob" });
        a.src = URL.createObjectURL(res.data);
      } catch {
        setError(true); setLoading(false);
        toast.error("Enregistrement indisponible.");
        return;
      }
      setLoading(false);
    }
    try { await a.play(); } catch { /* autoplay guard */ }
  };

  const seek = (e) => {
    const a = audioRef;
    if (!a || !dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    a.currentTime = ratio * dur;
    setCur(a.currentTime);
  };

  const pct = dur ? Math.min(100, (cur / dur) * 100) : 0;

  const download = async (e) => {
    e.stopPropagation();
    if (downloading) return;
    setDownloading(true);
    try {
      await downloadCallRecording(callId, fileLabel);
      toast.success("Enregistrement téléchargé.");
    } catch {
      toast.error("Téléchargement impossible.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div data-inline-player className="mt-2 flex max-w-[380px] items-center gap-2" data-testid={`inline-player-${callId}`}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); toggle(); }}
        disabled={loading}
        aria-label={playing ? "Pause" : "Lecture"}
        data-testid={`inline-play-${callId}`}
        className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[var(--shariti-orange)] text-white transition-transform hover:scale-105 active:scale-95 disabled:opacity-60"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 translate-x-[1px]" />}
      </button>
      <div
        onClick={(e) => { e.stopPropagation(); seek(e); }}
        className="group relative h-2 flex-1 cursor-pointer rounded-full bg-[var(--surface-2)]"
        data-testid={`inline-seek-${callId}`}
      >
        <div className="absolute inset-y-0 left-0 rounded-full bg-[var(--shariti-orange)]" style={{ width: `${pct}%` }} />
      </div>
      <span className="shrink-0 font-mono text-[11px] tabular-nums text-muted-ink" data-testid={`inline-time-${callId}`}>
        {error ? "indispo." : `${fmtClock(cur)} / ${fmtClock(dur)}`}
      </span>
      <button
        type="button"
        onClick={download}
        disabled={downloading}
        title="Télécharger l'audio de l'appel"
        aria-label="Télécharger l'enregistrement"
        data-testid={`inline-download-${callId}`}
        className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-muted-ink transition-colors hover:bg-[var(--surface-2)] hover:text-[var(--shariti-orange)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--shariti-orange)] disabled:opacity-60"
      >
        {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}

function EmptyCalls({ hasFilters, onClear }) {
  return (
    <div className="grid place-items-center rounded-2xl border border-dashed border-[var(--border-soft)] bg-white py-16 text-center" data-testid="calls-empty">
      <div className="grid h-14 w-14 place-items-center rounded-2xl bg-[rgba(238,114,46,0.1)] text-[var(--shariti-orange)]"><Phone className="h-6 w-6" /></div>
      {hasFilters ? (
        <>
          <h3 className="mt-4 text-base font-extrabold">Aucun appel pour ce filtre</h3>
          <p className="mt-1 max-w-sm text-sm text-muted-ink">Aucun appel ne correspond aux filtres sélectionnés depuis le cockpit.</p>
          <Button variant="outline" size="sm" onClick={onClear} className="mt-3" data-testid="calls-empty-clear">Réinitialiser les filtres</Button>
        </>
      ) : (
        <>
          <h3 className="mt-4 text-base font-extrabold">Aucun appel pour le moment</h3>
          <p className="mt-1 max-w-sm text-sm text-muted-ink">Depuis la prospection, cliquez sur « Appeler » pour lancer un appel. Il apparaîtra ici avec son enregistrement et son analyse IA.</p>
        </>
      )}
    </div>
  );
}

function CallDetailDialog({ call, open, onClose, onChanged, isAdmin, onHeard, onLink }) {
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioDownloading, setAudioDownloading] = useState(false);
  const [detail, setDetail] = useState(null);
  // Repère de rotation « en amont » du spot lié à l'appel (aide à la décision de booker).
  const [rotation, setRotation] = useState(null);

  useEffect(() => {
    setAudioUrl(null); setDetail(call || null);
    setNotes(call?.cdp_notes || "");
    if (call?.id) {
      api.get(`/calls/${call.id}`).then((r) => { setDetail(r.data); setNotes(r.data.cdp_notes || ""); }).catch(() => {});
      // Marque un message vocal / appel manqué comme écouté à l'ouverture du détail.
      if ((call.is_voicemail || call.direction === "inbound") && !call.heard) onHeard?.(call.id);
    }
    return () => { setAudioUrl((u) => { if (u) URL.revokeObjectURL(u); return null; }); };
    // eslint-disable-next-line
  }, [call?.id]);

  useEffect(() => {
    const sid = detail?.spot_id || call?.spot_id;
    if (!sid) { setRotation(null); return; }
    let cancelled = false;
    api.post("/planning/spots-rotation-status", { spot_ids: [sid] })
      .then((r) => { if (!cancelled) setRotation(r.data?.by_spot_id?.[sid] || null); })
      .catch(() => { if (!cancelled) setRotation(null); });
    return () => { cancelled = true; };
  }, [detail?.spot_id, call?.spot_id]);

  if (!call) return null;
  const d = detail || call;
  const a = d.ai_analysis || {};
  const sent = a.sentiment ? (SENTIMENT[a.sentiment] || SENTIMENT.neutre) : null;

  const loadAudio = async () => {
    setAudioLoading(true);
    try {
      const res = await api.get(`/calls/${d.id}/recording`, { responseType: "blob" });
      setAudioUrl(URL.createObjectURL(res.data));
    } catch {
      toast.error("Enregistrement indisponible.");
    } finally { setAudioLoading(false); }
  };

  const downloadAudio = async () => {
    if (audioDownloading) return;
    setAudioDownloading(true);
    try {
      await downloadCallRecording(d.id, `appel_${d.spot_name || d.contact_name || d.to_number || "inconnu"}_${String(d.started_at || "").slice(0, 10)}`);
      toast.success("Enregistrement téléchargé.");
    } catch {
      toast.error("Téléchargement impossible.");
    } finally { setAudioDownloading(false); }
  };

  const saveNotes = async () => {
    setSaving(true);
    try { await api.put(`/calls/${d.id}/notes`, { cdp_notes: notes }); toast.success("Notes enregistrées"); onChanged?.(); }
    catch { toast.error("Erreur"); } finally { setSaving(false); }
  };

  const analyze = async () => {
    setAnalyzing(true);
    try {
      if (notes.trim() !== (d.cdp_notes || "")) { await api.put(`/calls/${d.id}/notes`, { cdp_notes: notes }); }
      const { data } = await api.post(`/calls/${d.id}/analyze`);
      setDetail((p) => ({ ...p, ai_summary: data.summary, ai_analysis: data.analysis, cdp_notes: notes }));
      toast.success("Analyse IA générée");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Impossible d'analyser."); }
    finally { setAnalyzing(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="call-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PhoneOutgoing className="h-5 w-5 text-[var(--shariti-orange)]" />
            {d.contact_name || d.spot_name || "Appel"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Méta */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-ink">
            <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{d.to_number}</span>
            <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{fmtDateTime(d.started_at)}</span>
            {d.duration_sec != null && <span>Durée : {fmtDur(d.duration_sec)}</span>}
            <StatusPill status={d.status} />
            {d.spot_name && d.contact_name && <span>· {d.spot_name}</span>}
            {rotation && <RotationMarker status={rotation} />}
          </div>

          {/* Appel orphelin (sortant sans magasin) : proposition de rattachement */}
          {d.direction !== "inbound" && !d.is_voicemail && !d.is_demo && !d.spot_id && d.contact_status !== "invalid" && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[rgba(238,114,46,0.5)] bg-[rgba(238,114,46,0.06)] px-3 py-2.5" data-testid="detail-orphan-banner">
              <div className="flex items-start gap-2 text-[12px] font-semibold text-[#9A4A17]">
                <Link2 className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Cet appel n'est associé à aucun magasin. Rattachez-le pour ne perdre aucun contact.</span>
              </div>
              <Button size="sm" onClick={() => { onClose(); onLink?.(d); }} data-testid="detail-orphan-link"
                className="shrink-0 bg-[var(--shariti-orange)] hover:bg-[#E56622]">
                <Link2 className="mr-1.5 h-4 w-4" />Rattacher ce numéro
              </Button>
            </div>
          )}

          {/* Enregistrement */}
          <section>
            <div className="mb-2 flex items-center justify-between gap-2">
              <h4 className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-ink"><AudioLines className="h-3.5 w-3.5" />Enregistrement</h4>
              {d.recording_sid && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={downloadAudio}
                  disabled={audioDownloading}
                  data-testid="call-download-audio"
                  title="Télécharger l'audio de l'appel (pour l'écouter ou le faire écouter à un CDP)"
                  className="h-7 gap-1.5 px-2 text-[11px] font-semibold text-muted-ink hover:bg-[var(--surface-2)] hover:text-[var(--shariti-orange)]"
                >
                  {audioDownloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                  Télécharger
                </Button>
              )}
            </div>
            {d.recording_sid ? (
              audioUrl ? (
                <audio controls src={audioUrl} className="w-full" data-testid="call-audio-player" />
              ) : (
                <Button variant="outline" onClick={loadAudio} disabled={audioLoading} data-testid="call-load-audio">
                  {audioLoading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <PlayCircle className="mr-1.5 h-4 w-4" />}
                  Charger l'enregistrement
                </Button>
              )
            ) : (
              <p className="rounded-xl bg-[var(--surface-2)] px-3 py-2 text-xs text-muted-ink">Aucun enregistrement disponible pour cet appel.</p>
            )}
          </section>

          {/* Transcription */}
          <section>
            <div className="mb-2 flex items-center justify-between">
              <h4 className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-ink"><FileText className="h-3.5 w-3.5" />Transcription automatique</h4>
              <TranscriptionBadge status={d.transcription_status} confidence={d.transcript_confidence} />
            </div>
            {Array.isArray(d.transcript_segments) && d.transcript_segments.length > 0 ? (
              <div className="max-h-56 space-y-2 overflow-y-auto rounded-xl bg-[var(--surface-2)] p-3" data-testid="call-transcript">
                {d.transcript_segments.map((s, i) => {
                  const who = typeof s.speaker === "number" ? s.speaker : 0;
                  const isProspect = who !== 0;
                  return (
                    <div key={i} className={`flex gap-2 ${isProspect ? "" : "flex-row-reverse"}`}>
                      <span className={`mt-0.5 shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold ${isProspect ? "bg-[rgba(47,111,237,0.12)] text-[#2F6FED]" : "bg-[rgba(238,114,46,0.12)] text-[var(--shariti-orange)]"}`}>
                        {isProspect ? "Prospect" : "CDP"}
                      </span>
                      <p className={`rounded-xl px-3 py-1.5 text-sm leading-relaxed ${isProspect ? "bg-white" : "bg-[rgba(238,114,46,0.06)]"}`}>{s.text}</p>
                    </div>
                  );
                })}
              </div>
            ) : d.transcript ? (
              <p className="max-h-48 overflow-y-auto whitespace-pre-line rounded-xl bg-[var(--surface-2)] p-3 text-sm leading-relaxed" data-testid="call-transcript">{d.transcript}</p>
            ) : (
              <p className="rounded-xl bg-[var(--surface-2)] px-3 py-2 text-xs text-muted-ink">
                {d.transcription_status === "error" ? "La transcription a échoué (voir logs)."
                  : d.transcription_status === "disabled" ? "Transcription automatique désactivée (clé Deepgram manquante)."
                  : "Transcription non disponible pour le moment."}
              </p>
            )}
          </section>

          {/* Notes CDP */}
          <section>
            <h4 className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-ink"><NotebookPen className="h-3.5 w-3.5" />Notes du CDP</h4>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Vos notes sur l'appel…"
              className="min-h-[90px] text-sm" data-testid="call-notes-input" />
            <div className="mt-2 flex justify-end">
              <Button variant="outline" size="sm" onClick={saveNotes} disabled={saving} data-testid="call-save-notes">
                {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}Enregistrer les notes
              </Button>
            </div>
          </section>

          {/* Analyse IA */}
          <section className="rounded-2xl border border-[var(--border-soft)] bg-[rgba(238,114,46,0.03)] p-4">
            <div className="mb-3 flex items-center justify-between">
              <h4 className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-[var(--shariti-orange)]"><Sparkles className="h-3.5 w-3.5" />Analyse IA</h4>
              <Button size="sm" onClick={analyze} disabled={analyzing || (!notes.trim() && !d.transcript)}
                className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="call-analyze-btn">
                {analyzing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />}
                {d.ai_summary ? "Ré-analyser" : "Analyser"}
              </Button>
            </div>
            {d.ai_summary ? (
              <div className="space-y-3" data-testid="call-ai-block">
                <p className="rounded-xl bg-white p-3 text-sm leading-relaxed shadow-[var(--shadow-sm)]">{d.ai_summary}</p>
                <div className="flex flex-wrap items-center gap-2">
                  {sent && <span className="rounded-full px-2.5 py-1 text-[11px] font-bold" style={{ background: sent.bg, color: sent.fg }}>Sentiment : {sent.label}</span>}
                  {a.outcome && a.outcome !== "non détecté" && <span className="rounded-full bg-[rgba(238,114,46,0.1)] px-2.5 py-1 text-[11px] font-bold capitalize text-[var(--shariti-orange)]">Issue : {String(a.outcome).replaceAll("_", " ")}</span>}
                  {a.decideur_atteint && <span className="rounded-full bg-[var(--surface-2)] px-2.5 py-1 text-[11px] font-bold text-ink">Décideur atteint : {a.decideur_atteint}</span>}
                  {typeof a.score === "number" && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-[11px] font-bold text-ink shadow-[var(--shadow-sm)]">
                      <TrendingUp className="h-3 w-3 text-[var(--shariti-orange)]" />Potentiel : {a.score}/100
                    </span>
                  )}
                </div>

                {/* Faits analytiques */}
                <div className="grid gap-2 sm:grid-cols-2">
                  <Fact label="Interlocuteur" value={a.interlocuteur} />
                  <Fact label="Cause de refus" value={a.cause_refus} />
                  <Fact label="Date obtenue" value={a.date_obtenue} />
                  <Fact label="Prochaine action" value={a.next_action} strong />
                </div>

                {Array.isArray(a.engagements) && a.engagements.length > 0 && (
                  <FactList title="Engagements pris" items={a.engagements} icon={CheckCircle2} />
                )}
                {Array.isArray(a.verbatims) && a.verbatims.length > 0 && (
                  <div>
                    <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-muted-ink">Verbatims</div>
                    <div className="space-y-1.5">
                      {a.verbatims.map((v, i) => (
                        <p key={i} className="border-l-2 border-[var(--shariti-orange)] bg-white px-3 py-1.5 text-sm italic text-ink">« {v} »</p>
                      ))}
                    </div>
                  </div>
                )}
                {Array.isArray(a.key_points) && a.key_points.length > 0 && (
                  <ul className="space-y-1">
                    {a.key_points.map((k, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-ink">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--shariti-orange)]" />{k}
                      </li>
                    ))}
                  </ul>
                )}

                {/* Données captées pour la fiche spot */}
                {a.donnees_spot && (
                  <div className="rounded-xl border border-[var(--border-soft)] bg-white p-3">
                    <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-muted-ink">Données captées (fiche spot)</div>
                    <div className="grid gap-x-4 gap-y-1.5 sm:grid-cols-2">
                      {[["contact_nom", "Contact"], ["fonction", "Fonction"], ["telephone_direct", "Tél. direct"], ["email", "Email"], ["horaires_joignabilite", "Joignabilité"], ["remarques", "Remarques"]].map(([k, label]) => (
                        <div key={k} className="flex items-baseline justify-between gap-2 text-xs">
                          <span className="text-muted-ink">{label}</span>
                          <span className={`text-right font-semibold ${a.donnees_spot[k] === "non détecté" ? "text-muted-ink/60 italic" : "text-ink"}`}>{a.donnees_spot[k] || "—"}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-muted-ink">Ajoutez des notes (ou attendez la transcription), puis lancez l'analyse IA pour obtenir un résumé, le sentiment, l'issue et la prochaine action recommandée.</p>
            )}
          </section>

          {/* Zone de suppression totale (réservée à l'administrateur) */}
          {isAdmin && (
            <div className="flex items-center justify-between rounded-xl border border-[rgba(214,69,69,0.25)] bg-[rgba(214,69,69,0.04)] px-3 py-2.5">
              <div className="text-[11px] text-muted-ink">
                Supprime définitivement cet appel, son enregistrement et son analyse.
              </div>
              <DeleteCallButton callId={d.id} variant="full" onDeleted={() => { onClose(); onChanged?.(); }} />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TranscriptionBadge({ status, confidence }) {
  const map = {
    done: { label: "Transcrit", cls: "bg-[rgba(31,157,85,0.12)] text-[#0F5A2F]" },
    pending: { label: "Transcription…", cls: "bg-[rgba(238,114,46,0.12)] text-[var(--shariti-orange)]" },
    error: { label: "Échec", cls: "bg-[rgba(214,69,69,0.12)] text-[#B23434]" },
    disabled: { label: "Désactivée", cls: "bg-[var(--surface-2)] text-muted-ink" },
    no_recording: { label: "Sans audio", cls: "bg-[var(--surface-2)] text-muted-ink" },
  };
  const m = map[status];
  if (!m) return null;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${m.cls}`} data-testid="transcription-badge">
      {status === "pending" && <Loader2 className="h-3 w-3 animate-spin" />}
      {m.label}
      {status === "done" && typeof confidence === "number" && ` · ${Math.round(confidence * 100)}%`}
    </span>
  );
}

function Fact({ label, value, strong }) {
  const isEmpty = !value || value === "non détecté";
  return (
    <div className="rounded-xl border border-[var(--border-soft)] bg-white px-3 py-2">
      <div className="text-[10px] font-bold uppercase tracking-wide text-muted-ink">{label}</div>
      <div className={`mt-0.5 text-sm ${isEmpty ? "italic text-muted-ink/60" : strong ? "font-semibold text-ink" : "text-ink"}`}>
        {value || "non détecté"}
      </div>
    </div>
  );
}

function FactList({ title, items, icon: Icon }) {
  return (
    <div>
      <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-muted-ink">{title}</div>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-ink">
            {Icon ? <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#1F9D55]" /> : <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--shariti-orange)]" />}
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}
